/**
 * Auto-add native Automatic BXGY gifts + remove them atomically with the qualifier.
 * Rules: shop.metafields.d1x.bxgy_auto_add
 *
 * Critical: when the shopper deletes the main (buy) item, rewrite cart/change →
 * cart/update that zeros qualifier + gift together. That avoids the flash where
 * the gift jumps to full price, then disappears, then leaves a zombie drawer.
 *
 * Speed: on cart/add, fold missing gifts into the same request when we can resolve
 * the buy product id (PDP FormData / known cart). Reconcile remains the safety net.
 * If folding 422s (unpublished gift SKU), retry the original add without gifts.
 */
(function () {
  if (window.__d1BxgyAutoAddLoaded) return;
  window.__d1BxgyAutoAddLoaded = true;

  var PROP_KEY = '_d1_bxgy_gift';
  var LOADING_ATTR = 'data-d1-bxgy-loading';
  // ponytail: gifts stay while qualified — UI hides remove; never honor dismiss.
  var LOCK_GIFTS = true;
  var routesRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var rules = Array.isArray(window.__d1BxgyAutoAddRules)
    ? window.__d1BxgyAutoAddRules
    : [];
  if (!rules.length) return;

  var reconcileTimer = null;
  var reconciling = false;
  var internalFetch = false;
  var suppressAddsUntil = 0;
  var failedRules = Object.create(null);
  var lastQualifierKey = '';
  var lastKnownCart = null;
  var lastAddCoAddedGifts = false;

  ensureStyles();

  function ensureStyles() {
    if (document.getElementById('d1-bxgy-auto-add-style')) return;
    var style = document.createElement('style');
    style.id = 'd1-bxgy-auto-add-style';
    style.textContent =
      '[' +
      LOADING_ATTR +
      ']{opacity:.85}' +
      '[' +
      LOADING_ATTR +
      '] .d1-bxgy-loading__pulse{' +
      'display:inline-block;width:1.2rem;height:1.2rem;border:2px solid rgba(0,0,0,.15);' +
      'border-top-color:rgba(0,0,0,.55);border-radius:50%;' +
      'animation:d1-bxgy-spin .7s linear infinite;vertical-align:middle;margin-right:.6rem}' +
      '[' +
      LOADING_ATTR +
      '] .d1-bxgy-loading__label{font-size:1.3rem;color:rgba(var(--color-foreground),0.75)}' +
      '@keyframes d1-bxgy-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  function cartUrl(path) {
    return routesRoot.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function num(v) {
    var n = parseInt(String(v || ''), 10);
    return Number.isFinite(n) ? n : 0;
  }

  function itemProps(item) {
    return (item && item.properties) || {};
  }

  function isOurGiftLine(item, ruleId) {
    return String(itemProps(item)[PROP_KEY] || '') === String(ruleId);
  }

  function anyOurGiftLine(item) {
    return String(itemProps(item)[PROP_KEY] || '') !== '';
  }

  /** Paid / bundle lines that already cover a gift SKU (don't auto-add those). */
  function paidGiftCoverage(cart, rule) {
    var variantIds = (rule.gets && rule.gets.variantIds) || [];
    var productIds = (rule.gets && rule.gets.productIds) || [];
    var coveredVids = Object.create(null);
    var coveredPids = Object.create(null);
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isOurGiftLine(item, rule.id)) continue;
      var vid = num(item.variant_id || item.id);
      var pid = num(item.product_id);
      if (variantIds.indexOf(vid) !== -1) coveredVids[vid] = true;
      if (productIds.indexOf(pid) !== -1) coveredPids[pid] = true;
    }
    return { vids: coveredVids, pids: coveredPids };
  }

  function giftAlreadyInCart(cart, rule) {
    var cov = paidGiftCoverage(cart, rule);
    return Object.keys(cov.vids).length > 0 || Object.keys(cov.pids).length > 0;
  }

  /**
   * Gift lines to auto-add: one of each gets.variantId.
   * Uses max(configured quantity, variant count) so a stale metafield still on
   * quantity:1 after Admin was changed to 3 still adds every listed gift.
   */
  function missingGiftItems(cart, rule) {
    var variantIds = (rule.gets && rule.gets.variantIds) || [];
    var productIds = (rule.gets && rule.gets.productIds) || [];
    var configuredQty = num(rule.gets && rule.gets.quantity) || 1;
    // ponytail: multi-gift BXGY lists mean "one of each"; don't wait on middleware sync.
    var wantQty =
      variantIds.length > 1 ? Math.max(configuredQty, variantIds.length) : configuredQty;
    if (!variantIds.length || wantQty < 1) return [];

    var cov = paidGiftCoverage(cart, rule);
    var haveVids = Object.create(null);
    var haveQty = 0;
    ourGiftLines(cart, rule.id).forEach(function (g) {
      var vid = num(g.variant_id || g.id);
      var q = num(g.quantity) || 1;
      haveVids[vid] = (haveVids[vid] || 0) + q;
      haveQty += q;
    });

    // Paid/bundle coverage counts toward the gift quota for that SKU.
    Object.keys(cov.vids).forEach(function (vid) {
      if (!haveVids[vid]) {
        haveVids[vid] = 1;
        haveQty += 1;
      }
    });

    var need = wantQty - haveQty;
    if (need <= 0) return [];

    var items = [];
    for (var i = 0; i < variantIds.length && items.length < need; i++) {
      var vid = num(variantIds[i]);
      if (!vid || haveVids[vid]) continue;
      var pid = num(productIds[i]);
      if (cov.vids[vid] || (pid && cov.pids[pid])) continue;
      items.push({ id: vid, quantity: 1 });
      haveVids[vid] = 1;
    }
    return items;
  }

  function countBuyQty(cart, rule) {
    var buyIds = (rule.buys && rule.buys.productIds) || [];
    var set = Object.create(null);
    for (var i = 0; i < buyIds.length; i++) set[buyIds[i]] = true;
    var qty = 0;
    var items = (cart && cart.items) || [];
    for (var j = 0; j < items.length; j++) {
      var item = items[j];
      if (isOurGiftLine(item, rule.id)) continue;
      if (set[num(item.product_id)]) qty += num(item.quantity) || 1;
    }
    return qty;
  }

  function qualifierKey(cart) {
    var parts = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (anyOurGiftLine(item)) continue;
      parts.push(num(item.product_id) + 'x' + (num(item.quantity) || 1));
    }
    parts.sort();
    return parts.join('|');
  }

  function dismissKey(ruleId, token) {
    return 'd1_bxgy_dismiss_' + ruleId + '_' + (token || 'anon');
  }

  function isDismissed(ruleId, token) {
    if (LOCK_GIFTS) return false;
    try {
      return sessionStorage.getItem(dismissKey(ruleId, token)) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed(ruleId, token) {
    if (LOCK_GIFTS) return;
    try {
      sessionStorage.setItem(dismissKey(ruleId, token), '1');
    } catch (e) {}
  }

  function clearDismissed(ruleId, token) {
    try {
      sessionStorage.removeItem(dismissKey(ruleId, token));
    } catch (e) {}
  }

  function rawFetch(url, init) {
    internalFetch = true;
    return fetch(url, init).finally(function () {
      internalFetch = false;
    });
  }

  function fetchCart() {
    return rawFetch(cartUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  function drawerSectionIds() {
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map(function (s) {
        return s.id;
      });
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function paintDrawer(sectionResponse, cart, open) {
    var el = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (!el || typeof el.renderContents !== 'function') return false;
    if (!sectionResponse || !sectionResponse.sections) return false;

    var payload = {
      sections: sectionResponse.sections,
      id: sectionResponse.id,
      item_count: cart && typeof cart.item_count === 'number' ? cart.item_count : sectionResponse.item_count,
      items: (cart && cart.items) || sectionResponse.items || [],
      total_price:
        cart && typeof cart.total_price === 'number' ? cart.total_price : sectionResponse.total_price,
    };

    el.renderContents(payload, { open: open === true });
    return true;
  }

  function refreshCartUi() {
    var drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
      return Promise.resolve(drawerItems.onCartUpdate()).catch(function () {});
    }
    var ids = drawerSectionIds();
    if (!ids.length) return Promise.resolve();
    return Promise.all([
      rawFetch(window.location.pathname + '?sections=' + ids.join(',')).then(function (r) {
        return r.ok ? r.json() : null;
      }),
      fetchCart().catch(function () {
        return null;
      }),
    ]).then(function (pair) {
      if (!pair[0]) return;
      paintDrawer({ sections: pair[0] }, pair[1], false);
    });
  }

  function showGiftLoading() {
    hideGiftLoading();
    var tbody = document.querySelector('cart-drawer-items tbody');
    if (!tbody) return;
    var tr = document.createElement('tr');
    tr.className = 'cart-item d1-bxgy-loading-row';
    tr.setAttribute(LOADING_ATTR, '1');
    tr.innerHTML =
      '<td class="cart-item__media" colspan="1"></td>' +
      '<td class="cart-item__details" colspan="2">' +
      '<span class="d1-bxgy-loading__pulse" aria-hidden="true"></span>' +
      '<span class="d1-bxgy-loading__label">Adding free gift…</span>' +
      '</td>';
    tbody.appendChild(tr);
  }

  function hideGiftLoading() {
    document.querySelectorAll('[' + LOADING_ATTR + ']').forEach(function (el) {
      el.remove();
    });
  }

  function ourGiftLines(cart, ruleId) {
    return ((cart && cart.items) || []).filter(function (item) {
      return isOurGiftLine(item, ruleId);
    });
  }

  function noteGiftDismissals(before, after) {
    if (!before || !after) return;
    var token = after.token || before.token || '';
    rules.forEach(function (rule) {
      var had = ourGiftLines(before, rule.id).length > 0;
      var has = ourGiftLines(after, rule.id).length > 0;
      var stillQualifies =
        countBuyQty(after, rule) >= (num(rule.buys && rule.buys.minQuantity) || 1);
      if (had && !has && stillQualifies && !giftAlreadyInCart(after, rule)) {
        setDismissed(rule.id, token);
      }
    });
  }

  function addGift(rule, cart) {
    var items = missingGiftItems(cart, rule);
    if (!items.length) return Promise.resolve(null);
    var props = {};
    props[PROP_KEY] = String(rule.id);
    var payloadItems = items.map(function (it) {
      return { id: it.id, quantity: it.quantity, properties: props };
    });
    showGiftLoading();
    return rawFetch(cartUrl('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: payloadItems,
        sections: drawerSectionIds(),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        if (!r.ok) {
          failedRules[rule.id] = true;
          return null;
        }
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  function removeLine(key) {
    return rawFetch(cartUrl('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: key,
        quantity: 0,
        sections: drawerSectionIds(),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function addsAllowed() {
    return Date.now() >= suppressAddsUntil;
  }

  function parseJsonBody(init) {
    if (!init || init.body == null) return null;
    if (typeof init.body !== 'string') return null;
    try {
      return JSON.parse(init.body);
    } catch (e) {
      return null;
    }
  }

  function findTargetItem(cart, body) {
    if (!cart || !body) return null;
    var items = cart.items || [];
    if (body.id != null && body.id !== '') {
      var id = String(body.id);
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].key) === id || String(items[i].variant_id) === id || String(items[i].id) === id) {
          return items[i];
        }
      }
    }
    if (body.line != null) {
      var idx = num(body.line) - 1;
      if (idx >= 0 && idx < items.length) return items[idx];
    }
    return null;
  }

  /** Keys that must die with a qualifier qty change (so gift never flashes at full price). */
  function orphanGiftKeys(cart, target, nextQty) {
    if (!cart || !target || anyOurGiftLine(target)) return [];
    var afterItems = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.key === target.key) {
        if (nextQty > 0) {
          afterItems.push(Object.assign({}, item, { quantity: nextQty }));
        }
        continue;
      }
      afterItems.push(item);
    }
    var afterCart = { items: afterItems };
    var keys = [];
    var seen = Object.create(null);
    rules.forEach(function (rule) {
      var minQty = num(rule.buys && rule.buys.minQuantity) || 1;
      if (countBuyQty(afterCart, rule) >= minQty) return;
      ourGiftLines(cart, rule.id).forEach(function (g) {
        if (!seen[g.key]) {
          seen[g.key] = true;
          keys.push(g.key);
        }
      });
    });
    return keys;
  }

  /**
   * Rewrite Dawn's cart/change into cart/update that zeros qualifier + gifts together.
   * Dawn then paints the FINAL cart once — no full-price gift flash.
   */
  function maybeExpandChange(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!/\/cart\/change(\.js)?(\?|$)/.test(String(url))) return Promise.resolve(null);
    var body = parseJsonBody(init);
    if (!body || body.quantity == null) return Promise.resolve(null);
    var nextQty = num(body.quantity);

    return fetchCart()
      .then(function (cart) {
        lastKnownCart = cart;
        var target = findTargetItem(cart, body);
        if (!target) return null;
        var giftKeys = orphanGiftKeys(cart, target, nextQty);
        if (!giftKeys.length) return null;

        var updates = {};
        updates[target.key] = nextQty;
        giftKeys.forEach(function (k) {
          updates[k] = 0;
        });

        suppressAddsUntil = Date.now() + 2500;

        var sections = body.sections || drawerSectionIds();
        var headers = Object.assign(
          {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          (init && init.headers) || {}
        );

        return {
          url: cartUrl('cart/update.js'),
          init: {
            method: 'POST',
            credentials: (init && init.credentials) || 'same-origin',
            headers: headers,
            body: JSON.stringify({
              updates: updates,
              sections: sections,
              sections_url: body.sections_url || window.location.pathname,
            }),
          },
        };
      })
      .catch(function () {
        return null;
      });
  }

  function qtyAfterUpdates(item, updates) {
    if (!item || !updates) return num(item && item.quantity);
    if (Object.prototype.hasOwnProperty.call(updates, item.key)) return num(updates[item.key]);
    var vid = String(item.variant_id || item.id || '');
    if (vid && Object.prototype.hasOwnProperty.call(updates, vid)) return num(updates[vid]);
    return num(item.quantity);
  }

  /** Bundle remove uses cart/update (not change) — fold orphan gifts into that payload. */
  function orphanGiftKeysFromUpdates(cart, updates) {
    if (!cart || !updates) return [];
    var afterItems = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var nextQty = qtyAfterUpdates(item, updates);
      if (nextQty > 0) afterItems.push(Object.assign({}, item, { quantity: nextQty }));
    }
    var afterCart = { items: afterItems };
    var keys = [];
    var seen = Object.create(null);
    rules.forEach(function (rule) {
      var minQty = num(rule.buys && rule.buys.minQuantity) || 1;
      if (countBuyQty(afterCart, rule) >= minQty) return;
      ourGiftLines(cart, rule.id).forEach(function (g) {
        if (!seen[g.key]) {
          seen[g.key] = true;
          keys.push(g.key);
        }
      });
    });
    return keys;
  }

  function maybeExpandUpdate(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!/\/cart\/update(\.js)?(\?|$)/.test(String(url))) return Promise.resolve(null);
    var body = parseJsonBody(init);
    if (!body || !body.updates || typeof body.updates !== 'object') return Promise.resolve(null);

    return fetchCart()
      .then(function (cart) {
        lastKnownCart = cart;
        var giftKeys = orphanGiftKeysFromUpdates(cart, body.updates);
        if (!giftKeys.length) return null;

        var updates = Object.assign({}, body.updates);
        var added = false;
        giftKeys.forEach(function (k) {
          if (Object.prototype.hasOwnProperty.call(updates, k) && num(updates[k]) === 0) return;
          updates[k] = 0;
          added = true;
        });
        if (!added) return null;

        suppressAddsUntil = Date.now() + 2500;

        var headers = Object.assign(
          {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          (init && init.headers) || {}
        );

        return {
          url: cartUrl('cart/update.js'),
          init: {
            method: 'POST',
            credentials: (init && init.credentials) || 'same-origin',
            headers: headers,
            body: JSON.stringify({
              updates: updates,
              sections: body.sections || drawerSectionIds(),
              sections_url: body.sections_url || window.location.pathname,
            }),
          },
        };
      })
      .catch(function () {
        return null;
      });
  }

  function formDataProperties(fd) {
    var properties = {};
    if (!fd || typeof fd.forEach !== 'function') return properties;
    fd.forEach(function (value, key) {
      var m = /^properties\[([^\]]+)\]$/.exec(String(key));
      if (m) properties[m[1]] = value;
    });
    return properties;
  }

  function normalizeSections(raw) {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (raw == null || raw === '') return null;
    return String(raw)
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  /** Resolve product id for a variant from DOM / warm cart (no network). */
  function resolveProductId(variantId, hint) {
    var hinted = num(hint);
    if (hinted) return hinted;
    var vid = num(variantId);
    if (!vid) return 0;

    var btn = document.querySelector(
      '.card__atc-btn[data-variant-id="' + vid + '"][data-product-id], [data-variant-id="' + vid + '"][data-product-id]'
    );
    if (btn) {
      var fromBtn = num(btn.getAttribute('data-product-id'));
      if (fromBtn) return fromBtn;
    }

    var roots = document.querySelectorAll('[data-product-id]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var pid = num(root.getAttribute('data-product-id'));
      if (!pid) continue;
      var input = root.querySelector('input[name="id"], select[name="id"]');
      if (input && num(input.value) === vid) return pid;
      if (root.getAttribute('data-variant-id') && num(root.getAttribute('data-variant-id')) === vid) {
        return pid;
      }
    }

    var items = (lastKnownCart && lastKnownCart.items) || [];
    for (var j = 0; j < items.length; j++) {
      if (num(items[j].variant_id || items[j].id) === vid) return num(items[j].product_id);
    }
    return 0;
  }

  /**
   * Parse cart/add body into line stubs.
   * Supports JSON {items:[…]} / {id,…} and FormData (Dawn product-form).
   */
  function parseAddRequest(init) {
    if (!init || init.body == null) return null;

    if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
      var fd = init.body;
      var id = num(fd.get('id'));
      if (!id) return null;
      var props = formDataProperties(fd);
      if (props[PROP_KEY]) return null; // already a gift add
      return {
        items: [
          {
            id: id,
            quantity: num(fd.get('quantity')) || 1,
            properties: props,
            product_id: resolveProductId(id, fd.get('product-id') || fd.get('product_id')),
          },
        ],
        sections: normalizeSections(fd.get('sections')),
        sections_url: fd.get('sections_url') || window.location.pathname,
      };
    }

    var body = parseJsonBody(init);
    if (!body) return null;

    var lines = [];
    if (Array.isArray(body.items) && body.items.length) {
      for (var i = 0; i < body.items.length; i++) {
        var it = body.items[i] || {};
        var vid = num(it.id);
        if (!vid) continue;
        var ip = it.properties || {};
        if (ip[PROP_KEY]) return null; // gift batch — leave alone
        lines.push({
          id: vid,
          quantity: num(it.quantity) || 1,
          properties: ip,
          product_id: resolveProductId(vid, it.product_id || it.productId),
        });
      }
    } else if (body.id != null) {
      var singleProps = body.properties || {};
      if (singleProps[PROP_KEY]) return null;
      var singleId = num(body.id);
      if (!singleId) return null;
      lines.push({
        id: singleId,
        quantity: num(body.quantity) || 1,
        properties: singleProps,
        product_id: resolveProductId(singleId, body.product_id || body.productId),
      });
    }

    if (!lines.length) return null;
    return {
      items: lines,
      sections: normalizeSections(body.sections),
      sections_url: body.sections_url || window.location.pathname,
    };
  }

  function projectCartAfterAdd(cart, additions) {
    var items = ((cart && cart.items) || []).map(function (item) {
      return Object.assign({}, item);
    });
    for (var i = 0; i < additions.length; i++) {
      var add = additions[i];
      items.push({
        id: add.id,
        variant_id: add.id,
        product_id: num(add.product_id),
        quantity: num(add.quantity) || 1,
        properties: add.properties || {},
        key: 'pending-' + add.id + '-' + i,
      });
    }
    return {
      items: items,
      token: (cart && cart.token) || '',
    };
  }

  function giftsForProjectedCart(projected) {
    var out = [];
    var seenVid = Object.create(null);
    rules.forEach(function (rule) {
      if (!rule || failedRules[rule.id]) return;
      if (isDismissed(rule.id, projected.token)) return;
      var minQty = num(rule.buys && rule.buys.minQuantity) || 1;
      if (countBuyQty(projected, rule) < minQty) return;
      missingGiftItems(projected, rule).forEach(function (g) {
        var vid = num(g.id);
        if (!vid || seenVid[vid]) return;
        seenVid[vid] = true;
        var props = {};
        props[PROP_KEY] = String(rule.id);
        out.push({ id: vid, quantity: num(g.quantity) || 1, properties: props });
      });
    });
    return out;
  }

  /**
   * Fold BXGY gifts into the same cart/add when buy product ids are known.
   * Uses warm lastKnownCart (no extra cart.js) so ATC stays one round-trip.
   */
  function maybeExpandAdd(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!/\/cart\/add(\.js)?(\?|$)/.test(String(url))) return Promise.resolve(null);
    if (!addsAllowed()) return Promise.resolve(null);

    var parsed = parseAddRequest(init);
    if (!parsed || !parsed.items.length) return Promise.resolve(null);

    // Need at least one resolvable buy product id to know qualification.
    var resolved = 0;
    for (var i = 0; i < parsed.items.length; i++) {
      if (num(parsed.items[i].product_id)) resolved += 1;
    }
    if (!resolved) return Promise.resolve(null);

    var cart = lastKnownCart || { items: [], token: '' };
    var projected = projectCartAfterAdd(cart, parsed.items);
    var gifts = giftsForProjectedCart(projected);
    if (!gifts.length) return Promise.resolve(null);

    var payloadItems = parsed.items
      .map(function (it) {
        var row = { id: it.id, quantity: it.quantity };
        if (it.properties && Object.keys(it.properties).length) row.properties = it.properties;
        return row;
      })
      .concat(gifts);

    var payload = { items: payloadItems };
    var sections = parsed.sections && parsed.sections.length ? parsed.sections : drawerSectionIds();
    if (sections && sections.length) {
      payload.sections = sections;
      payload.sections_url = parsed.sections_url || window.location.pathname;
    }

    lastAddCoAddedGifts = true;

    var headers = Object.assign(
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      (init && init.headers) || {}
    );
    // FormData path deletes Content-Type; force JSON now.
    headers['Content-Type'] = 'application/json';

    return Promise.resolve({
      url: cartUrl('cart/add.js'),
      init: {
        method: 'POST',
        credentials: (init && init.credentials) || 'same-origin',
        headers: headers,
        body: JSON.stringify(payload),
      },
    });
  }

  function reconcileCart() {
    if (reconciling) {
      scheduleReconcile(400);
      return Promise.resolve();
    }
    reconciling = true;
    var didMutate = false;
    var lastSectionResponse = null;
    var beforeCart = lastKnownCart;

    return fetchCart()
      .then(function (cart) {
        lastKnownCart = cart;
        var token = cart.token || '';
        var qKey = qualifierKey(cart);
        if (qKey !== lastQualifierKey) {
          lastQualifierKey = qKey;
          if (addsAllowed()) {
            rules.forEach(function (rule) {
              clearDismissed(rule.id, token);
            });
          }
        }

        if (beforeCart) noteGiftDismissals(beforeCart, cart);

        var chain = Promise.resolve();
        rules.forEach(function (rule) {
          chain = chain.then(function () {
            if (failedRules[rule.id]) return;
            var minQty = num(rule.buys && rule.buys.minQuantity) || 1;
            var buyQty = countBuyQty(cart, rule);
            var gifts = ourGiftLines(cart, rule.id);
            var qualifies = buyQty >= minQty;

            if (!qualifies) {
              clearDismissed(rule.id, token);
              if (!gifts.length) return;
              didMutate = true;
              var rem = Promise.resolve();
              gifts.forEach(function (g) {
                rem = rem.then(function () {
                  return removeLine(g.key).then(function (res) {
                    if (res) lastSectionResponse = res;
                  });
                });
              });
              return rem.then(function () {
                return fetchCart().then(function (c) {
                  cart = c;
                  lastKnownCart = c;
                });
              });
            }

            // Drop auto-gift lines that duplicate a paid/bundle gift SKU; keep the rest.
            var afterDupes = Promise.resolve();
            if (gifts.length) {
              var cov = paidGiftCoverage(cart, rule);
              var dupes = gifts.filter(function (g) {
                var vid = num(g.variant_id || g.id);
                var pid = num(g.product_id);
                return cov.vids[vid] || cov.pids[pid];
              });
              if (dupes.length) {
                didMutate = true;
                var remDup = Promise.resolve();
                dupes.forEach(function (g) {
                  remDup = remDup.then(function () {
                    return removeLine(g.key).then(function (res) {
                      if (res) lastSectionResponse = res;
                    });
                  });
                });
                afterDupes = remDup.then(function () {
                  return fetchCart().then(function (c) {
                    cart = c;
                    lastKnownCart = c;
                  });
                });
              }
            }

            return afterDupes.then(function () {
              var missing = missingGiftItems(cart, rule);
              if (!missing.length) return;
              if (!addsAllowed()) return;
              if (isDismissed(rule.id, token)) return;

              return addGift(rule, cart).then(function (res) {
                if (!res) return;
                didMutate = true;
                lastSectionResponse = res;
                return fetchCart().then(function (c) {
                  cart = c;
                  lastKnownCart = c;
                });
              });
            });
          });
        });

        return chain.then(function () {
          return cart;
        });
      })
      .then(function (cart) {
        hideGiftLoading();
        if (!didMutate) return;

        if (lastSectionResponse && lastSectionResponse.sections) {
          paintDrawer(lastSectionResponse, cart, false);
        } else {
          return refreshCartUi();
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'd1-bxgy',
            cartData: cart || lastKnownCart,
          });
        }
      })
      .catch(function () {})
      .then(function () {
        reconciling = false;
        hideGiftLoading();
      });
  }

  function scheduleReconcile(delayMs) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileCart();
    }, typeof delayMs === 'number' ? delayMs : 200);
  }

  function isCartPath(url) {
    try {
      return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
    } catch (e) {
      return false;
    }
  }

  function onExternalCartMutation(url) {
    var u = String(url || '');
    // Atomic update already removed gifts — don't fight Dawn's paint.
    if (/\/cart\/update(\.js)?(\?|$)/.test(u)) {
      suppressAddsUntil = Date.now() + 2500;
      scheduleReconcile(600);
      return;
    }
    if (/\/cart\/(change|clear)/.test(u)) {
      suppressAddsUntil = Date.now() + 2500;
      scheduleReconcile(500);
      return;
    }
    if (/\/cart\/add/.test(u)) {
      if (lastAddCoAddedGifts) {
        lastAddCoAddedGifts = false;
        // Gift already in the same add — light safety reconcile, no loading row.
        scheduleReconcile(250);
        return;
      }
      showGiftLoading();
      scheduleReconcile(100);
    }
  }

  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      if (internalFetch) return origFetch(input, init);

      var url =
        typeof input === 'string'
          ? input
          : input && typeof input.url === 'string'
            ? input.url
            : '';

      if (!isCartPath(url)) return origFetch(input, init);

      // Expand qualifier deletes / co-add gifts on ATC.
      return maybeExpandChange(input, init)
        .then(function (expanded) {
          if (expanded) return expanded;
          return maybeExpandUpdate(input, init);
        })
        .then(function (expanded) {
          if (expanded) return expanded;
          return maybeExpandAdd(input, init);
        })
        .then(function (expanded) {
          var req = expanded
            ? origFetch(expanded.url, expanded.init)
            : origFetch(input, init);
          var foldedAdd =
            expanded && /\/cart\/add(\.js)?(\?|$)/.test(String(expanded.url || ''));

          if (!foldedAdd) {
            req
              .then(function () {
                onExternalCartMutation(expanded ? expanded.url : url);
              })
              .catch(function () {
                onExternalCartMutation(expanded ? expanded.url : url);
              });
            return req;
          }

          // Dead gift SKUs 422 the whole batch. Retry the original add so the
          // bundle still lands; reconcile tries gifts on their own.
          return req.then(function (res) {
            if (res && res.ok) {
              onExternalCartMutation(expanded.url);
              return res;
            }
            lastAddCoAddedGifts = false;
            var retry = origFetch(input, init);
            retry
              .then(function () {
                onExternalCartMutation(url);
              })
              .catch(function () {
                onExternalCartMutation(url);
              });
            return retry;
          }).catch(function () {
            lastAddCoAddedGifts = false;
            var retry = origFetch(input, init);
            retry
              .then(function () {
                onExternalCartMutation(url);
              })
              .catch(function () {
                onExternalCartMutation(url);
              });
            return retry;
          });
        });
    };
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source === 'd1-bxgy') return;
      if (event.source === 'cart-items') {
        suppressAddsUntil = Date.now() + 2500;
        if (event.cartData) {
          if (lastKnownCart) noteGiftDismissals(lastKnownCart, event.cartData);
          lastKnownCart = event.cartData;
          if (event.cartData.item_count === 0) {
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.syncEmptyState === 'function') drawer.syncEmptyState(true);
            else if (drawer) drawer.classList.add('is-empty');
          }
        }
        scheduleReconcile(400);
        return;
      }
      scheduleReconcile(150);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    scheduleReconcile(50);
  });
  window.addEventListener('pageshow', function () {
    scheduleReconcile(50);
  });

  if (document.readyState !== 'loading') scheduleReconcile(50);
})();
